import {useContext, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Box, Button, Collapse, Grid, Typography} from '@mui/material';

import {BoxList} from '../../../../../../components/BoxList/BoxList';
import {CultureTypeImage} from '../../../../../../components/CultureTypeImage/CultureTypeImage';
import {LanguageContext} from '../../../../../../contexts/LanguageContext/LanguageContext';
import {greenColors} from '../../../../../../layouts/theme/designSystem';
import {CultureInformation} from '../../types/CultureInformationTypes';

interface CultureTypeInformationProps {
  cultureInformation: CultureInformation;
  /** When true, always render the full description and hide the show more/less toggle. */
  alwaysExpanded?: boolean;
}

export default function CultureTypeInformation({cultureInformation, alwaysExpanded}: CultureTypeInformationProps) {
  const {t} = useTranslation();
  const currentLanguage = useContext(LanguageContext);
  const [isExpanded, setIsExpanded] = useState(false);
  const expanded = alwaysExpanded || isExpanded;

  const cultureType = cultureInformation.cultureType.name[currentLanguage];
  const fullParagraphOne = cultureInformation.cultureType.description.paragraphOne[currentLanguage];
  const fullParagraphTwo = cultureInformation.cultureType.description.paragraphTwo[currentLanguage];
  const previewParagraphOne = useMemo(
    () => (fullParagraphOne.length > 600 ? `${fullParagraphOne.slice(0, 600)}...` : fullParagraphOne),
    [fullParagraphOne]
  );
  const words = useMemo(
    () => cultureInformation.cultureType.keywords[currentLanguage].split(',').map((str) => str.trim()),
    [cultureInformation.cultureType.keywords, currentLanguage]
  );

  // Memoize the image to prevent unnecessary re-renders
  const cultureTypeImage = useMemo(() => {
    return (
      <CultureTypeImage sx={{marginTop: '20px', marginBottom: '65px'}} src={cultureInformation.cultureType.imageUrl} />
    );
  }, [cultureInformation.cultureType.imageUrl]);

  return (
    <Grid container gap={{xs: 2, md: 4}}>
      <Grid item flexBasis={{xs: '100%', md: '30%'}}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            borderRadius: '10px',
            backgroundColor: 'background.paper',
            backgroundImage: 'linear-gradient(to top left, #FFFFFF 60%, #E7E2DE 100%)',
            padding: '32px',
          }}
        >
          <Typography variant='body1' sx={{color: greenColors[700]}}>
            {t('companyCultureDashboard.cultureType')}
          </Typography>
          <Typography variant='h1'>{cultureType}</Typography>
          <Grid item display={'flex'} justifyContent={'center'}>
            {cultureTypeImage}
          </Grid>
        </Box>
      </Grid>

      <Grid item flexBasis={{xs: '100%', md: 'calc(70% - 32px)'}}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            borderRadius: '10px',
            backgroundColor: 'background.paper',
            padding: '32px',
          }}
        >
          <Typography variant='h3' sx={{mb: 2}}>
            {t('companyCultureDashboard.cultureTypeDescriptionHeader', {cultureType: cultureType})}
          </Typography>
          <Box mb={3}>
            <BoxList data={words} stackOnMobile />
          </Box>
          <Typography
            variant='body1'
            mb={expanded ? 2 : 0}
            sx={{transition: 'margin-bottom 200ms ease'}}
          >
            {expanded ? fullParagraphOne : previewParagraphOne}
          </Typography>
          <Collapse in={expanded} timeout='auto' unmountOnExit>
            <Typography variant='body1' mb={2}>
              {fullParagraphTwo}
            </Typography>
          </Collapse>
          {!alwaysExpanded && (
            <Button
              variant='text'
              size='small'
              onClick={() => setIsExpanded((prev) => !prev)}
              sx={{alignSelf: 'flex-start', textTransform: 'none', mt: 2, p: 0}}
            >
              {isExpanded ? t('common.showLess', 'Show less') : t('common.showMore', 'Show more')}
            </Button>
          )}
        </Box>
      </Grid>
    </Grid>
  );
}
